import { Anchor, Center, Group, Image, Paper, SegmentedControl, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import type { PublicBranding } from '@/api/sso';
import { useTranslation } from 'react-i18next';
import { useSystemInfo } from '@/api/system';
import { Logo } from '@/components/Logo';
import { legalLinks } from '@/lib/legal';

/** Rahmen der Anmeldeseiten; mit Workspace-Branding (WS-06) Logo, Name und Akzentfarbe des Workspaces. */
export function AuthLayout({ children, branding }: { children: ReactNode; branding?: PublicBranding | null }) {
  const { t, i18n } = useTranslation();
  const systemInfo = useSystemInfo();
  return (
    <Center mih="100dvh" p="md" bg="var(--mantine-color-gray-light)">
      <Stack w="100%" maw={420} gap="lg">
        <Center>
          {branding?.logoUrl ? (
            <Stack gap={4} align="center">
              <Image src={branding.logoUrl} h={56} w="auto" fit="contain" alt={branding.name} />
              <Text size="sm" c="dimmed">
                {branding.name}
              </Text>
            </Stack>
          ) : branding ? (
            <Stack gap={4} align="center">
              <Logo size={40} />
              <Text fw={600}>{branding.name}</Text>
            </Stack>
          ) : (
            <Logo size={40} />
          )}
        </Center>
        <Paper withBorder shadow="sm" p="xl" radius="md" style={branding?.brandColor ? { borderTop: `4px solid ${branding.brandColor}` } : undefined}>
          {children}
        </Paper>
        <Group justify="center">
          <SegmentedControl
            size="xs"
            aria-label={t('profile.language')}
            value={i18n.language.startsWith('en') ? 'en' : 'de'}
            onChange={(v) => void i18n.changeLanguage(v)}
            data={[
              { value: 'de', label: 'Deutsch' },
              { value: 'en', label: 'English' },
            ]}
          />
        </Group>
        <Group justify="center" gap="md">
          <Anchor href={legalLinks(i18n.language).imprint} size="xs" c="dimmed">
            {t('legal.imprint')}
          </Anchor>
          <Anchor href={legalLinks(i18n.language).privacy} size="xs" c="dimmed">
            {t('legal.privacy')}
          </Anchor>
          <Anchor href={legalLinks(i18n.language).terms} size="xs" c="dimmed">
            {t('legal.terms')}
          </Anchor>
        </Group>
        <Text size="xs" c="dimmed" ta="center">
          {systemInfo.data
            ? t('login.version', { version: systemInfo.data.version })
            : systemInfo.isError
              ? t('login.backendUnavailable')
              : ' '}
        </Text>
      </Stack>
    </Center>
  );
}
